// Developer-mode backend override. Lets a build point at an arbitrary server
// (a laptop on the LAN, a staging box) instead of the origin it was served
// from — the Android/PWA equivalent of Android's hidden "developer options".
//
// It is deliberately synchronous (localStorage, not the async KV store): the
// composition root reads the override at import time so a single reload is
// enough to switch backends. Nothing here is a security boundary — the entered
// URL only replaces the REST base and WS endpoint the app talks to; the
// engine still verifies signed endpoint-list updates and TOFU-pins keys.

import type { EndpointList } from '@merkury/protocol';

const ORIGIN_KEY = 'merkury.dev.server';
const UNLOCK_KEY = 'merkury.dev.unlocked';
/** Taps on the version footer needed to reveal the developer section. */
export const DEV_UNLOCK_TAPS = 7;

function ls(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Storage can throw (disabled cookies, sandboxed iframe) — treat as absent.
    return undefined;
  }
}

/**
 * Normalize user input into an origin (`scheme://host[:port]`, no path/slash).
 * Accepts a bare `host:port` (assumes http, the common local-backend case) or
 * a full `http(s)://…` URL. Returns undefined if it cannot be parsed.
 */
export function normalizeServerOrigin(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  // If the input carries a scheme, it must be http(s); a bare `host:port`
  // (no `://`) is assumed to be http, the common local-backend case.
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed);
  if (hasScheme && !/^https?:\/\//i.test(trimmed)) return undefined;
  const withScheme = hasScheme ? trimmed : `http://${trimmed}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return undefined;
    if (!u.hostname) return undefined;
    return `${u.protocol}//${u.host}`;
  } catch {
    return undefined;
  }
}

/** The configured dev backend origin, or undefined when not in dev mode. */
export function getDevServerOrigin(): string | undefined {
  const raw = ls()?.getItem(ORIGIN_KEY) ?? undefined;
  return raw ? normalizeServerOrigin(raw) : undefined;
}

/** Persist a dev backend origin. Returns the normalized value it stored. */
export function setDevServerOrigin(raw: string): string | undefined {
  const origin = normalizeServerOrigin(raw);
  if (!origin) return undefined;
  ls()?.setItem(ORIGIN_KEY, origin);
  return origin;
}

/** Remove the override; the app falls back to its baked-in origin next reload. */
export function clearDevServerOrigin(): void {
  ls()?.removeItem(ORIGIN_KEY);
}

/** The REST base for the override, e.g. `http://192.168.1.5:8080/api/v1`. */
export function devApiBaseUrl(): string | undefined {
  const origin = getDevServerOrigin();
  return origin ? `${origin}/api/v1` : undefined;
}

/** A single-endpoint list pointing WS at the override (ws for http, wss for https). */
export function devEndpointList(): EndpointList | undefined {
  const origin = getDevServerOrigin();
  if (!origin) return undefined;
  const u = new URL(origin);
  const wsScheme = u.protocol === 'https:' ? 'wss' : 'ws';
  return {
    version: 1,
    signedBy: 'dev-override',
    signature: 'A'.repeat(86) + '==',
    endpoints: [{ kind: 'domain', url: `${wsScheme}://${u.host}/ws` }],
  };
}

export type ProbeResult =
  | { status: 'ok'; latencyMs: number }
  | { status: 'http-error'; httpStatus: number }
  | { status: 'cors-blocked' }
  | { status: 'unreachable' }
  | { status: 'timeout' }
  | { status: 'invalid' };

export const PROBE_TIMEOUT_MS = 5000;

function fetchWithTimeout(
  fetchFn: typeof fetch,
  url: string,
  init: RequestInit,
): { res: Promise<Response>; timedOut: () => boolean } {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
  const res = fetchFn(url, { ...init, cache: 'no-store', signal: ctrl.signal }).finally(() =>
    clearTimeout(timer),
  );
  return { res, timedOut: () => ctrl.signal.aborted };
}

/**
 * Probe a candidate backend by fetching its `/healthz`. A plain CORS fetch
 * cannot distinguish "host unreachable" from "reachable but CORS-blocked"
 * (both reject with a TypeError), so on failure an opaque `no-cors` request
 * retries: if that one succeeds, the server is up but missing
 * `DEV_CORS_ORIGINS` — worth telling the developer apart from a dead host.
 */
export async function probeServer(
  raw: string,
  fetchFn: typeof fetch = fetch,
): Promise<ProbeResult> {
  const origin = normalizeServerOrigin(raw);
  if (!origin) return { status: 'invalid' };
  const url = `${origin}/healthz`;
  const started = Date.now();
  const cors = fetchWithTimeout(fetchFn, url, {});
  try {
    const res = await cors.res;
    return res.ok
      ? { status: 'ok', latencyMs: Date.now() - started }
      : { status: 'http-error', httpStatus: res.status };
  } catch {
    if (cors.timedOut()) return { status: 'timeout' };
    const opaque = fetchWithTimeout(fetchFn, url, { mode: 'no-cors' });
    try {
      await opaque.res;
      return { status: 'cors-blocked' };
    } catch {
      return opaque.timedOut() ? { status: 'timeout' } : { status: 'unreachable' };
    }
  }
}

/** Whether the hidden developer section has been unlocked on this device. */
export function isDevUnlocked(): boolean {
  return ls()?.getItem(UNLOCK_KEY) === '1' || getDevServerOrigin() !== undefined;
}

export function setDevUnlocked(unlocked: boolean): void {
  if (unlocked) ls()?.setItem(UNLOCK_KEY, '1');
  else ls()?.removeItem(UNLOCK_KEY);
}
